"use client";

import { DrupalNode } from "next-drupal";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { formatDateToUS, resolveLink } from "@/utils";
import { MdChevronRight } from "react-icons/md";
import { FaChevronRight } from "react-icons/fa6";
import { config } from "@/lib/config";
import slugify from "slugify";

interface NewsSectionProps {
  data: DrupalNode;
}

const NewsSection: React.FC<NewsSectionProps> = ({ data }) => {
  const [newsItems, setNewsItems] = useState<DrupalNode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(
          `/api/news/filter?type=${data?.field_news_type || ""}`,
        );
        const result = await response.json();
        setNewsItems(result);
      } catch (error) {
        console.error("Failed to fetch news:", error);
      } finally {
        setLoading(false);
      }
    };

    if (data?.field_news_type) {
      fetchNews();
    } else {
      setLoading(false);
    }
  }, [data?.field_news_type]);

  if (loading) {
    return (
      <div className="py-20 mini-container">
        <div className="w-48 h-10 bg-gray-200 animate-pulse mx-auto mb-12 rounded-lg" />
        <div className="flex gap-4 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex-1 h-[420px] bg-gray-200 animate-pulse rounded-2xl"
            />
          ))}
        </div>
      </div>
    );
  }

  if (newsItems.length === 0) return null;

  return (
    <div className="pt-[60px] pb-[80px] betweenMobileTab:py-16 mobileMax:py-10 bg-white">
      <div className="mini-container">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0 }}
          className="remove-animation-fluctuation relative text-center mb-[80px] mobileMax:mb-8"
        >
          <h2
            className="desktop:text-[52px] font-poppins font-semibold
              text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)]
              leading-normal text-center text-clip text-[48px]
              mobileMax:text-[28px] mb-10"
          >
            {data?.field_title}
          </h2>

          {data?.field_navigation_button?.uri && (
            <Link
              href={resolveLink(data?.field_navigation_button?.uri)}
              className="desktop:flex hidden absolute z-10 right-0 top-[25px] 
              items-center justify-center font-poppins font-[600] 
              text-transparent bg-clip-text 
              bg-[linear-gradient(to_right,#316FA8,#003350)] 
              text-xmedium rounded-[10px] underline"
            >
              {data?.field_navigation_button?.title}
              <FaChevronRight className="text-small ml-[25px] text-[#003350]" />
            </Link>
          )}
        </motion.div>

        <div className="!flex justify-center betweenMobileTab:justify-start gap-4 flex-wrap">
          {newsItems?.map((newsData: DrupalNode, index: number) => {
            const redirectLink = resolveLink(
              newsData.field_news_link ? newsData.field_news_link : "#",
            );

            const imageUrl = newsData?.field_image?.uri?.url
              ? `${config.apiBase}${newsData.field_image.uri.url}`
              : newsData?.field_news_media_url;

            const isVideo =
              newsData?.field_news_media_url?.includes("youtube") ||
              newsData?.field_news_media_url?.includes("vimeo");

            const isEvent = newsData?.field_n_resource?.name === "Event";
            const basePath = isEvent ? "/events" : "/news";
            const sluggedLink = newsData.title
              ? `${basePath}/${slugify(`${newsData.title} ${newsData.id}`, { lower: true, strict: true })}`
              : `${basePath}/${newsData.id}`;

            return (
              <Link
                href={
                  newsData?.field_mark_as_external_source
                    ? redirectLink
                    : sluggedLink
                }
                key={index}
                className="relative w-[96%] minMobile:w-[96%] aboveMinMobile:w-[96%] mobileMax:w-[96%] 
                betweenMobileTab:w-[48%] lieTablets:w-[48%] aboveLaptop:w-[48%] 
                largeDesk:w-[30%] padPro:w-[30%] desktop:w-[30%] 
                h-[420px] rounded-2xl overflow-hidden shadow-md group duration-700 
                hover:scale-105 hover:shadow-2xl transition-all"
                target={
                  newsData?.field_mark_as_external_source ? "_blank" : "_self"
                }
              >
                {/* Background Image */}
                {imageUrl?.includes("mission-efficiency.pantheonsite.io") ? (
                  <div className="absolute inset-0">
                    <Image
                      src={imageUrl || "/static/images/fallback.png"}
                      alt="news image"
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw,
                           (max-width: 1200px) 50vw,
                           33vw"
                      priority={index < 2}
                    />
                  </div>
                ) : (
                  <img
                    src={`${imageUrl}`}
                    alt="news image"
                    className="object-cover h-full w-full"
                  />
                )}

                {/* Overlay gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-[150px] bg-gradient-to-t from-black/80 to-transparent" />

                {/* Category badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#EFF5F3] text-[#003350] shadow">
                    {newsData?.field_n_resource?.name || "News"}
                  </span>
                </div>

                {/* Content */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="text-[14px] mb-1 opacity-80">
                    {newsData?.field_card_date &&
                      formatDateToUS(newsData?.field_card_date)}
                  </p>
                  <h3 className="text-[18px] font-semibold leading-snug line-clamp-2">
                    {newsData?.title}
                  </h3>

                  <div className="mt-2 inline-flex items-center text-sm text-white">
                    Learn More
                    <MdChevronRight className="w-[18px] h-[18px] ml-0.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0,
          }}
          className="remove-animation-fluctuation flex justify-center"
        >
          {data?.field_navigation_button?.uri && (
            <Link
              href={resolveLink(data?.field_navigation_button?.uri)}
              className="flex desktop:hidden betweenMobileTab:mt-10 mt-[60px] bg-[linear-gradient(to_right,#316FA8,#003350)] text-clip items-center justify-center font-poppins font-[600] !text-[#11055F] text-xmedium rounded-[10px] !underline mobileMax:min-w-full mobileMax:text-small "
            >
              {data?.field_navigation_button?.title}{" "}
              <FaChevronRight className="text-small ml-1 text-[#003350]" />
            </Link>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default NewsSection;
